"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar, X } from "lucide-react";

interface DateRangeSelectorProps {
  dateRange: "week" | "month" | "quarter" | "year" | "custom";
  onDateRangeChange: (range: "week" | "month" | "quarter" | "year" | "custom") => void;
  customStartDate?: string;
  customEndDate?: string;
  onCustomDateChange?: (startDate: string, endDate: string) => void;
}

export function DateRangeSelector({
  dateRange,
  onDateRangeChange,
  customStartDate = "",
  customEndDate = "",
  onCustomDateChange,
}: DateRangeSelectorProps) {
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(customStartDate);
  const [tempEndDate, setTempEndDate] = useState(customEndDate);

  const handleApplyCustom = () => {
    if (tempStartDate && tempEndDate && onCustomDateChange) {
      onCustomDateChange(tempStartDate, tempEndDate);
      onDateRangeChange("custom");
      setIsCustomOpen(false);
    }
  };

  const handleClearCustom = () => {
    setTempStartDate("");
    setTempEndDate("");
    if (onCustomDateChange) {
      onCustomDateChange("", "");
    }
    onDateRangeChange("month");
    setIsCustomOpen(false);
  };

  const presets: Array<{ value: "week" | "month" | "quarter" | "year"; label: string }> = [
    { value: "week", label: "Week" },
    { value: "month", label: "Month" },
    { value: "quarter", label: "Quarter" },
    { value: "year", label: "Year" },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {presets.map((preset) => (
        <Button
          key={preset.value}
          variant={dateRange === preset.value ? "default" : "outline"}
          size="sm"
          onClick={() => onDateRangeChange(preset.value)}
        >
          {preset.label}
        </Button>
      ))}
      <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={dateRange === "custom" ? "default" : "outline"}
            size="sm"
            className="flex items-center gap-1"
          >
            <Calendar className="h-4 w-4" />
            {dateRange === "custom" && customStartDate && customEndDate
              ? `${customStartDate} - ${customEndDate}`
              : "Custom"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              {(tempStartDate || tempEndDate) && (
                <Button variant="ghost" size="sm" onClick={handleClearCustom}>
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleApplyCustom}
                disabled={!tempStartDate || !tempEndDate}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

